/**
 * WSH Native Video Coordinator
 *
 * Wires native Horizon <video> elements (e.g. block-level <deferred-media>
 * videos) into the Brief 5 media coordinator event contract.
 *
 * WSH-controlled video surfaces have their own coordination — see
 *   dynamic-video-carousel.js
 *   video-walkthrough.js
 *   wsh-daw-ready-sessions-in-action.js (theme)
 *   wsh-daw-collection-sessions-video.js (theme)
 *
 * This file covers everything else: bare <video> elements dropped into
 * theme JSON blocks or sections that are NOT inside a WSH-controlled
 * surface. Discovered via Brief 5 hotfix on /collections/logic-pro-sample-packs
 * — a native Horizon "video" block (autoplay + loop + controls) had no
 * coordination.
 *
 * Excluded from coordination:
 *   - Videos inside any WSH-scoped surface ([data-wsh-section], etc.)
 *   - Videos inside <video-background-component> (Horizon's hero background)
 */
(function () {
  'use strict';

  var WSH_SCOPED_SELECTOR = '[data-wsh-section], dynamic-video-carousel, video-walkthrough, video-popup-model, wsh-buy-box, wsh-audio-manager, wsh-soundbar, wsh-product-player';
  var HORIZON_BACKGROUND_SELECTOR = 'video-background-component';
  var SOURCE_ID = 'native-video';

  function isCoordinatable(video) {
    if (!video || video.tagName !== 'VIDEO') return false;
    if (video.closest(WSH_SCOPED_SELECTOR)) return false;
    if (video.closest(HORIZON_BACKGROUND_SELECTOR)) return false;
    return true;
  }

  function findCoordinatable() {
    var all = document.querySelectorAll('video');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (isCoordinatable(all[i])) out.push(all[i]);
    }
    return out;
  }

  function pauseAllNativeVideos() {
    var videos = findCoordinatable();
    for (var i = 0; i < videos.length; i++) {
      if (!videos[i].paused) videos[i].pause();
    }
  }

  // Module-scope listeners (bound once for script lifetime).
  document.addEventListener('videos:pause', pauseAllNativeVideos);
  document.addEventListener('wsh:video:play', function (e) {
    if (e && e.detail && e.detail.source !== SOURCE_ID) pauseAllNativeVideos();
  });

  function bindVideoPlay(video) {
    if (!isCoordinatable(video)) return;
    if (video.dataset.wshCoordBound === 'true') return;
    video.dataset.wshCoordBound = 'true';
    video.addEventListener('play', function () {
      document.dispatchEvent(new CustomEvent('wsh:video:play', {
        detail: { source: SOURCE_ID }
      }));
      var mgr = document.querySelector('wsh-audio-manager');
      if (mgr && typeof mgr.pause === 'function') mgr.pause();
    });
  }

  function bindAll() {
    var videos = findCoordinatable();
    for (var i = 0; i < videos.length; i++) {
      bindVideoPlay(videos[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  document.addEventListener('shopify:section:load', bindAll);

  // MutationObserver: <deferred-media> lazily inserts its <video> child on
  // first interaction / viewport entry, so the initial DOM scan misses it.
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      var shouldRebind = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'VIDEO' || (node.querySelector && node.querySelector('video'))) {
            shouldRebind = true;
            break;
          }
        }
        if (shouldRebind) break;
      }
      if (shouldRebind) bindAll();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
